import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Checkbox, Typography } from "@mui/material";
import { CHART_INK, OTHER_COLOR, withOwnRanking } from "../../../../../utils/chartPalette";
import { useSpeciesColors } from "../useSpeciesColors";
import { useChartExport } from "../useChartExport";
import { Segmented } from "./Segmented";
import { arcPath, layoutLabels, percent, pieDataRows, pieGroups, pieSlices } from "./pieModel";

const NUMBER = new Intl.NumberFormat();
// the chart's own font, so labels measure as they draw (an SVG otherwise
// inherits whatever the page's root font is)
const FONT_FAMILY = "Roboto, Helvetica, Arial, sans-serif";
const FONT = `12px ${FONT_FAMILY}`;

// a label's width in px as the chart draws it
let measureCanvas;
const textWidth = (text) => {
  try {
    measureCanvas = measureCanvas ?? document.createElement("canvas");
    const ctx = measureCanvas.getContext("2d");
    ctx.font = FONT;
    return ctx.measureText(text).width;
  } catch (e) {
    return text.length * 7;
  }
};
const MODES = [
  { label: "Genus", value: "genus" },
  { label: "Species", value: "species" },
];

// the plot's size, followed as the report area changes
const useSize = (ref) => {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    if (!ref.current) return undefined;
    const update = () => setSize({ width: ref.current.clientWidth, height: ref.current.clientHeight });
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
  return size;
};

/**
 * A donut of the shown species or genera with every slice labelled outside
 * the ring (labels pushed apart so none overlap), the total in the middle.
 */
const Donut = ({ slices, total, width, height, mode }) => {
  const [active, setActive] = useState(null);
  if (!width || !height || !slices.length) return null;
  const cx = width / 2;
  const cy = height / 2;
  // leave room beside the ring for the longest label and its leader
  const labelRoom = Math.max(0, ...slices.map((p) => textWidth(`${p.label}  ${percent(p.share)}`))) + 36;
  const r = Math.max(60, Math.min(width / 2 - labelRoom, height / 2 - 30));
  const inner = r * 0.58;
  const placed = layoutLabels(slices, { cx, cy, r, gap: 16, top: 12, bottom: height - 8 });
  const focus = placed.find((p) => p.key === active);

  return (
    <svg width={width} height={height} role="img" aria-label={`Specimens by ${mode}`} fontFamily={FONT_FAMILY}>
      {placed.map((p) => (
        <path
          key={p.key}
          d={arcPath(cx, cy, inner, p.key === active ? r + 4 : r, p.a0, p.a1)}
          fill={p.color}
          stroke="#ffffff"
          strokeWidth={2}
          onMouseEnter={() => setActive(p.key)}
          onMouseLeave={() => setActive(null)}
        >
          <title>{`${p.label}: ${NUMBER.format(p.total)} (${percent(p.share)})`}</title>
        </path>
      ))}
      {placed.map((p) => {
        const x = p.textX;
        const endX = x + (p.right ? -4 : 4);
        return (
          <g key={`l-${p.key}`} opacity={active && active !== p.key ? 0.45 : 1}>
            <polyline
              points={`${p.anchor[0]},${p.anchor[1]} ${p.elbow[0]},${p.y} ${endX},${p.y}`}
              fill="none"
              stroke={CHART_INK.axis}
              strokeWidth={1}
            />
            <text x={x} y={p.y} dy="0.35em" textAnchor={p.right ? "start" : "end"} fontSize={12} fill={CHART_INK.primary}>
              {p.label}
              <tspan fill={CHART_INK.secondary}>{`  ${percent(p.share)}`}</tspan>
            </text>
          </g>
        );
      })}
      <text x={cx} y={cy - 8} textAnchor="middle" fontSize={focus ? 13 : 22} fontWeight={500} fill={CHART_INK.primary}>
        {focus ? focus.label : NUMBER.format(total)}
      </text>
      <text x={cx} y={cy + 14} textAnchor="middle" fontSize={12} fill={CHART_INK.secondary}>
        {focus ? `${NUMBER.format(focus.total)} · ${percent(focus.share)}` : "specimens"}
      </text>
    </svg>
  );
};

const cell = { py: 0.5, px: 1, borderBottom: 1, borderColor: "divider", fontSize: "0.8125rem", whiteSpace: "nowrap" };
const num = { ...cell, textAlign: "right", fontVariantNumeric: "tabular-nums" };

/**
 * The species summary: a donut by genus or by species beside a table of
 * every species under its genus with its count and share of what is shown.
 * Unticking a species or a genus takes it out of the chart and the shares.
 */
export const SpeciesPie = ({ model, title, registerChartExport }) => {
  const orgColors = useSpeciesColors();
  const [mode, setMode] = useState("species");
  const [hidden, setHidden] = useState(() => new Set());
  const plotRef = useRef(null);
  const { width, height } = useSize(plotRef);

  const groups = useMemo(() => pieGroups(model), [model]);
  const colors = useMemo(
    () =>
      withOwnRanking(
        orgColors,
        groups
          .flatMap((g) => g.species)
          .sort((a, b) => b.total - a.total)
          .map((s) => s.label)
      ),
    [orgColors, groups]
  );
  const { slices, total } = useMemo(() => pieSlices(groups, hidden, mode, colors), [groups, hidden, mode, colors]);
  const swatch = (label, category) =>
    category === "species" && colors.isOther(label) ? OTHER_COLOR : colors.colorOf(label);

  const setShown = (labels, show) =>
    setHidden((prev) => {
      const next = new Set(prev);
      labels.forEach((l) => (show ? next.delete(l) : next.add(l)));
      return next;
    });

  const getExportSpec = useCallback(
    () => ({
      title,
      legend: slices.map((s) => ({ label: `${s.label}: ${NUMBER.format(s.total)} (${percent(s.share)})`, color: s.color })),
      dataRows: pieDataRows(groups, hidden),
    }),
    [title, slices, groups, hidden]
  );
  useChartExport(registerChartExport, getExportSpec);

  if (!groups.length) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <Typography color="text.secondary">No specimens match these filters.</Typography>
      </Box>
    );
  }

  const shareOf = (n) => (total ? percent(n / total) : "–");

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", height: "100%", minHeight: 0, px: { xs: 2, md: 3 }, py: 2, gap: 3 }}>
      <Box sx={{ flex: "1 1 520px", display: "flex", flexDirection: "column", minHeight: 360 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Segmented label="Slices by" options={MODES} value={mode} onChange={setMode} />
          {hidden.size > 0 && (
            <Typography variant="body2" color="text.secondary">
              {hidden.size} hidden
            </Typography>
          )}
        </Box>
        <Box ref={plotRef} sx={{ flex: 1, minHeight: 320 }}>
          {total ? (
            <Donut slices={slices} total={total} width={width} height={height} mode={mode} />
          ) : (
            <Typography color="text.secondary" sx={{ p: 6, textAlign: "center" }}>
              Every species is hidden.
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ flex: "0 1 400px", minWidth: 300, display: "flex", flexDirection: "column", minHeight: 0, maxHeight: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            By genus and species
          </Typography>
          <Button size="small" disabled={!hidden.size} onClick={() => setHidden(new Set())}>
            Show all
          </Button>
        </Box>
        <Box sx={{ overflow: "auto", border: 1, borderColor: "divider", borderRadius: 1 }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }} aria-label="Species summary by genus">
            <Box component="thead">
              <Box component="tr">
                <Box component="th" sx={{ ...cell, width: 40 }} />
                <Box component="th" sx={{ ...cell, textAlign: "left", fontWeight: 500 }}>
                  Genus and species
                </Box>
                <Box component="th" sx={{ ...num, fontWeight: 500 }}>
                  Count
                </Box>
                <Box component="th" sx={{ ...num, fontWeight: 500 }}>
                  Share
                </Box>
              </Box>
            </Box>
            <Box component="tbody">
              {groups.map((g) => {
                const labels = g.species.map((s) => s.label);
                const shownHere = g.species.filter((s) => !hidden.has(s.label));
                const single = g.category !== "species" || g.species.length === 1;
                const groupTotal = shownHere.reduce((n, s) => n + s.total, 0);
                const groupRow = (
                  <Box component="tr" key={g.genus} sx={{ bgcolor: single ? "transparent" : "action.hover" }}>
                    <Box component="td" sx={cell}>
                      <Checkbox
                        size="small"
                        sx={{ p: 0.25 }}
                        checked={shownHere.length > 0}
                        indeterminate={shownHere.length > 0 && shownHere.length < labels.length}
                        onChange={(e) => setShown(labels, e.target.checked)}
                        inputProps={{ "aria-label": `Show ${g.genus}` }}
                      />
                    </Box>
                    <Box component="th" scope="row" sx={{ ...cell, textAlign: "left", fontWeight: single ? 400 : 500 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {single && (
                          <Box component="span" sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: swatch(g.species[0].label, g.category) }} />
                        )}
                        <Box component="span" sx={{ fontStyle: g.category === "species" ? "italic" : "normal" }}>
                          {g.category === "species" && g.species.length === 1 ? g.species[0].label : g.genus}
                        </Box>
                      </Box>
                    </Box>
                    <Box component="td" sx={{ ...num, fontWeight: single ? 400 : 500 }}>
                      {NUMBER.format(groupTotal)}
                    </Box>
                    <Box component="td" sx={{ ...num, fontWeight: single ? 400 : 500 }}>
                      {shownHere.length ? shareOf(groupTotal) : "–"}
                    </Box>
                  </Box>
                );
                if (single) return groupRow;
                return [
                  groupRow,
                  ...g.species.map((s) => {
                    const off = hidden.has(s.label);
                    return (
                      <Box component="tr" key={s.label} sx={{ color: off ? "text.disabled" : "text.primary" }}>
                        <Box component="td" sx={cell}>
                          <Checkbox
                            size="small"
                            sx={{ p: 0.25, ml: 2 }}
                            checked={!off}
                            onChange={(e) => setShown([s.label], e.target.checked)}
                            inputProps={{ "aria-label": `Show ${s.label}` }}
                          />
                        </Box>
                        <Box component="th" scope="row" sx={{ ...cell, textAlign: "left", fontWeight: 400, pl: 4 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box component="span" sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: swatch(s.label, g.category), opacity: off ? 0.4 : 1 }} />
                            <Box component="span" sx={{ fontStyle: "italic" }}>
                              {s.epithet}
                            </Box>
                          </Box>
                        </Box>
                        <Box component="td" sx={num}>
                          {NUMBER.format(s.total)}
                        </Box>
                        <Box component="td" sx={num}>
                          {off ? "–" : shareOf(s.total)}
                        </Box>
                      </Box>
                    );
                  }),
                ];
              })}
            </Box>
            <Box component="tfoot">
              <Box component="tr">
                <Box component="td" sx={cell} />
                <Box component="th" scope="row" sx={{ ...cell, textAlign: "left", fontWeight: 500 }}>
                  Total shown
                </Box>
                <Box component="td" sx={{ ...num, fontWeight: 600 }}>
                  {NUMBER.format(total)}
                </Box>
                <Box component="td" sx={{ ...num, fontWeight: 600 }}>
                  {total ? "100%" : "–"}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
